/**
 * /dashboard/hiring/requisitions/$id — Requisition detail / edit
 * (Phase H.8).
 *
 * IMPORTANT — there is NO GET /hiring/requisitions/{id} endpoint. The
 * single requisition is DERIVED from the role-scoped list query — the
 * same pattern as expenses/$id / leave detail.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/hiring/requisitions/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, GitBranch } from "lucide-react";
import { useRequisitions } from "@/hooks/use-hiring";
import { useDirectory } from "@/hooks/use-directory";
import { toApiError } from "@/lib/api";
import {
  EMPLOYMENT_TYPE_LABELS,
  fmtDate,
  REQUISITION_STATUS_LABELS,
  requisitionTone,
} from "@/lib/hiring-format";
import { HiringModuleShell } from "@/components/shared/hiring-module-shell";
import { HiringFeatureGate } from "@/components/hiring/hiring-feature-gate";
import { HrAdminGate } from "@/components/hiring/hr-admin-gate";
import { RequisitionForm } from "@/components/hiring/requisition-form";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/hiring/requisitions/$id/")({
  component: RequisitionDetailRoute,
});

function RequisitionDetail({ id }: { readonly id: number }) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const reqs = useRequisitions();
  const dir = useDirectory();
  const req = reqs.data?.requisitions.find((r) => r.id === id);

  if (reqs.isLoading) {
    return (
      <HiringModuleShell title="Requisition">
        <Card>
          <CardContent className="space-y-4 py-8">
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </HiringModuleShell>
    );
  }

  if (reqs.isError) {
    return (
      <HiringModuleShell title="Requisition">
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load this requisition</AlertTitle>
          <AlertDescription>
            {toApiError(reqs.error).message}
          </AlertDescription>
        </Alert>
      </HiringModuleShell>
    );
  }

  if (!req) {
    return (
      <HiringModuleShell title="Requisition">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">
              This requisition isn&apos;t available. It may have been
              closed, or you may not have access to it.
            </p>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/hiring/requisitions" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to Requisitions
              </Link>
            </Button>
          </CardContent>
        </Card>
      </HiringModuleShell>
    );
  }

  return (
    <HiringModuleShell
      title={req.title}
      description={
        [
          req.department,
          req.location,
          EMPLOYMENT_TYPE_LABELS[req.employment_type],
        ]
          .filter(Boolean)
          .join(" · ") || "Edit this requisition"
      }
      actions={
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link
              to="/app/$tenantSlug/hiring/requisitions/$id/pipeline"
              params={{tenantSlug, id: String(req.id)}}
            >
              <GitBranch className="h-4 w-4" />
              Pipeline
            </Link>
          </Button>
          <Button asChild variant="ghost" className="gap-1.5">
            <Link to="/app/$tenantSlug/hiring/requisitions" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              All requisitions
            </Link>
          </Button>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          Status:{" "}
          <StatusBadge
            label={REQUISITION_STATUS_LABELS[req.status]}
            tone={requisitionTone(req.status)}
          />
        </span>
        <span>Openings: {req.openings}</span>
        <span>
          Hiring manager:{" "}
          {req.hiring_manager_id != null
            ? dir.label(req.hiring_manager_id)
            : "Unassigned"}
        </span>
        <span>
          Public:{" "}
          {req.is_public ? "Yes (when open)" : "No"}
        </span>
        <span>Created {fmtDate(req.created_at)}</span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Edit requisition</CardTitle>
        </CardHeader>
        <CardContent>
          <RequisitionForm existing={req} />
        </CardContent>
      </Card>
    </HiringModuleShell>
  );
}

function RequisitionDetailRoute() {
  const { id } = Route.useParams();
  const numId = Number(id);

  return (
    <HiringFeatureGate>
      <HrAdminGate
        title="Requisition"
        description="Edit this requisition."
      >
        {Number.isFinite(numId) && numId > 0 ? (
          <RequisitionDetail id={numId} />
        ) : (
          <HiringModuleShell title="Requisition">
            <Card>
              <CardContent className="py-16 text-center">
                <p className="text-sm text-muted-foreground">
                  Invalid requisition.
                </p>
              </CardContent>
            </Card>
          </HiringModuleShell>
        )}
      </HrAdminGate>
    </HiringFeatureGate>
  );
}
